import * as THREE from 'three';
import { generateGalaxy, generateDust, generateBackdrop } from './generate';
import { buildLatticeGrid, fillLattice, type LatticeGrid } from './lattice';
import { discTexture, STAR_STOPS, SOFT_STOPS } from './textures';
import { SUN_RADIUS_KPC } from './potential';

const additive = { transparent: true, blending: THREE.AdditiveBlending, depthWrite: false } as const;

const STAR_SIZE = 0.13;
/**
 * Two thousand overlapping 4 kpc sprites pile up to a haze whose peak, in linear light, would sit above the bloom
 * threshold along the whole arms. The haze is meant to be felt rather than seen, so it is held well under it.
 */
const DUST_OPACITY = 0.3;
const BACKDROP_SIZE = 4;

/**
 * Everything the "Wider" chapter draws, with no behaviour of its own: the stars, the dust, the core glow, the well
 * lattice, the Sun's ring and the Sun. All additive, no lights; emissive values above 1.0 are meant to bloom.
 */
export class GalaxyWorld {
  readonly stars: THREE.Points;
  readonly dust: THREE.Points;
  readonly backdrop: THREE.Points;
  readonly core: THREE.Sprite;
  readonly well: THREE.LineSegments;
  readonly ring: THREE.Line;
  readonly sun: THREE.Sprite;
  readonly starCount: number;
  private grid: LatticeGrid;
  private wellPos: THREE.BufferAttribute;
  private wellCol: THREE.BufferAttribute;
  private disposables: { dispose(): void }[] = [];

  constructor(readonly scene: THREE.Scene) {
    const starTex = discTexture(STAR_STOPS, 64);
    const softTex = discTexture(SOFT_STOPS, 128);
    this.disposables.push(starTex, softTex);

    // the galaxy: disc, bar and bulge in one draw call
    const cloud = generateGalaxy();
    this.starCount = cloud.positions.length / 3;
    const sg = new THREE.BufferGeometry();
    sg.setAttribute('position', new THREE.BufferAttribute(cloud.positions, 3));
    sg.setAttribute('color', new THREE.BufferAttribute(cloud.colors, 3));
    const sm = new THREE.PointsMaterial({ size: STAR_SIZE, map: starTex, vertexColors: true, sizeAttenuation: true, ...additive });
    this.stars = new THREE.Points(sg, sm);
    this.disposables.push(sg, sm);

    // large dim dust along the arms
    const d = generateDust();
    const dg = new THREE.BufferGeometry();
    dg.setAttribute('position', new THREE.BufferAttribute(d.positions, 3));
    dg.setAttribute('color', new THREE.BufferAttribute(d.colors, 3));
    const dm = new THREE.PointsMaterial({ size: 4.0, map: softTex, vertexColors: true, sizeAttenuation: true, ...additive, opacity: DUST_OPACITY });
    this.dust = new THREE.Points(dg, dm);
    this.disposables.push(dg, dm);

    // the glowing core
    const cm = new THREE.SpriteMaterial({ map: softTex, color: 0xffb070, opacity: 0.55, ...additive });
    this.core = new THREE.Sprite(cm);
    this.core.scale.set(7, 5.5, 1);
    this.disposables.push(cm);

    // distant stars
    const b = generateBackdrop();
    const bg = new THREE.BufferGeometry();
    bg.setAttribute('position', new THREE.BufferAttribute(b.positions, 3));
    bg.setAttribute('color', new THREE.BufferAttribute(b.colors, 3));
    const bm = new THREE.PointsMaterial({ size: BACKDROP_SIZE, map: starTex, vertexColors: true, sizeAttenuation: true, ...additive });
    this.backdrop = new THREE.Points(bg, bm);
    this.disposables.push(bg, bm);

    // the well: indexed line segments over shared nodes, rewritten in place
    this.grid = buildLatticeGrid();
    const wg = new THREE.BufferGeometry();
    this.wellPos = new THREE.BufferAttribute(new Float32Array(this.grid.nodeCount * 3), 3);
    this.wellCol = new THREE.BufferAttribute(new Float32Array(this.grid.nodeCount * 3), 3);
    this.wellPos.setUsage(THREE.DynamicDrawUsage);
    this.wellCol.setUsage(THREE.DynamicDrawUsage);
    wg.setAttribute('position', this.wellPos);
    wg.setAttribute('color', this.wellCol);
    wg.setIndex(new THREE.BufferAttribute(this.grid.index, 1));
    const wm = new THREE.LineBasicMaterial({ vertexColors: true, opacity: 0.55, ...additive });
    this.well = new THREE.LineSegments(wg, wm);
    this.well.frustumCulled = false;
    this.disposables.push(wg, wm);

    // the Sun's orbit and the Sun
    const ringPts: number[] = [];
    for (let i = 0; i <= 256; i++) { const a = (i / 256) * Math.PI * 2; ringPts.push(SUN_RADIUS_KPC * Math.cos(a), SUN_RADIUS_KPC * Math.sin(a), 0.02); }
    const rg = new THREE.BufferGeometry();
    rg.setAttribute('position', new THREE.Float32BufferAttribute(ringPts, 3));
    const rm = new THREE.LineBasicMaterial({ color: 0x8cc1de, opacity: 0.8, ...additive });
    this.ring = new THREE.Line(rg, rm);
    this.disposables.push(rg, rm);

    const sunMat = new THREE.SpriteMaterial({ map: starTex, ...additive });
    sunMat.color.setRGB(1.8, 1.75, 1.6); // above 1.0: the Sun blooms
    this.sun = new THREE.Sprite(sunMat);
    this.sun.scale.set(1.3, 1.3, 1);
    this.sun.position.set(SUN_RADIUS_KPC, 0, 0.05);
    this.disposables.push(sunMat);

    // draw order is irrelevant for additive blending, but keep the backdrop first for tidiness
    scene.add(this.backdrop, this.well, this.stars, this.dust, this.core, this.ring, this.sun);
    this.setHalo(0.15);
  }

  /** Rewrite the lattice for halo strength hk. Touches 9,216 nodes. */
  setHalo(hk: number) {
    fillLattice(this.grid, hk, this.wellPos.array as Float32Array, this.wellCol.array as Float32Array);
    this.wellPos.needsUpdate = true;
    this.wellCol.needsUpdate = true;
  }

  setSunAngle(a: number) {
    this.sun.position.set(SUN_RADIUS_KPC * Math.cos(a), SUN_RADIUS_KPC * Math.sin(a), 0.05);
  }

  /**
   * Points below a pixel are still drawn as a whole pixel, so a zoomed-out galaxy would brighten. Scale the stars'
   * opacity by the area they would cover so the picture keeps its brightness at any distance.
   * @param heightPx internal render height, @param distance camera distance to the target
   */
  setViewScale(heightPx: number, distance: number) {
    const px = (STAR_SIZE * heightPx * 0.5) / Math.max(distance, 1);
    (this.stars.material as THREE.PointsMaterial).opacity = Math.max(0.05, Math.min(1, px * px));
  }

  dispose() {
    this.scene.remove(this.backdrop, this.well, this.stars, this.dust, this.core, this.ring, this.sun);
    for (const d of this.disposables) d.dispose();
    this.disposables = [];
  }
}
